import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import type { Control } from 'react-hook-form';
import z from 'zod';
import { useFormExtended } from '../../../lib/hooks/use-form-extended.ts';
import type { SalesDraftSalesConfigOutput } from '../../../server/types/drafts-types.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { TransactionAddedItemEventSource } from '../../analytics/types/analytics-events.ts';
import { useDraftContext } from './use-draft-context.ts';
import { useToasts } from './use-toasts.ts';

export type ProductResult = {
    article_group_id?: string | null;
    article_group_name?: string | null;
    product_variant_id: string;
    product_variant_name: string;
};

export type UseAddProductFormOptions = {
    onSuccess?: () => void;
    product: ProductResult;
    source: TransactionAddedItemEventSource;
};

export type UseAddProductFormHook = {
    control: Control<AddProductFormValues>;
    onSubmit: () => void;
    isLoading: boolean;
    isUpdating: boolean;
    salesConfig: SalesDraftSalesConfigOutput | undefined;
    salesUnit: string | undefined;
};

export type AddProductFormValues = z.infer<typeof addProductFormSchema>;

const addProductFormSchema = z.object({
    quantity: z.number(),
});

export function useAddProductForm({
    onSuccess,
    product,
    source,
}: UseAddProductFormOptions): UseAddProductFormHook {
    const { draft, setDraft } = useDraftContext({ refetchOnMount: false });
    const sendEvent = useAnalytics();
    const { showItemAddedToast, showUpdateErrorToast } = useToasts();

    const { control, handleSubmit, reset } = useFormExtended({
        mode: 'all',
        defaultValues: {
            quantity: 1,
        },
        resolver: zodResolver(addProductFormSchema),
    });

    const { data: salesConfig, isLoading } =
        api.platoUser.query.salesDraft.getSalesDraft.getSalesConfig.useQuery({
            product_variant_id: product.product_variant_id,
        });

    const salesUnit = salesConfig?.quantity_unit_sales ?? salesConfig?.package_unit;

    useEffect(() => {
        if (salesConfig) {
            reset({
                quantity: salesConfig.sales_units_per_package_unit ?? 1,
            });
        }
    }, [reset, salesConfig]);

    const { mutate, isPending } = api.platoUser.command.salesDraft.lineItems.createNormalLineItem.useMutation(
        {
            onError: showUpdateErrorToast,
            onSuccess: (data, { quantity }) => {
                setDraft(data);

                if (source === 'catalogue' || source === 'product_detail_page') {
                    showItemAddedToast({ quantity });
                }

                onSuccess?.();
            },
        },
    );

    const onSubmit = handleSubmit(({ quantity }: AddProductFormValues): void => {
        if (draft && salesUnit) {
            sendEvent(`item_added_from_${source}`, {
                article_group_id: product.article_group_id ?? undefined,
                draft_id: draft.draft_order_id,
                product_variant_id: product.product_variant_id,
                quantity,
            });
            mutate({
                draft_order_id: draft.draft_order_id,
                product_variant_id: product.product_variant_id,
                quantity,
                quantity_unit: salesUnit,
                shipping_type_id: draft.shipping_type_id ?? '',
                storage_id: draft.storage_id ?? '',
            });
        }
    });

    return {
        control,
        isLoading,
        isUpdating: isPending,
        onSubmit,
        salesConfig,
        salesUnit,
    };
}
